import { Routes, Route, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { healthAPI } from './lib/api.js';
import { isAuthenticated } from './lib/auth.js';
import { Toaster } from 'react-hot-toast';
import Shell from './components/layout/Shell.jsx';
import ApiDownBanner from './components/common/ApiDownBanner.jsx';
import Protected from './components/common/Protected.jsx';
import ErrorBoundary from './components/common/ErrorBoundary.jsx';
import NotFoundPage from './components/common/NotFoundPage.jsx';
import SRPFLoginPage from './features/auth/SRPFLoginPage.jsx';
import SRPFLoginDemo from './features/auth/LoginDemo.jsx';
import DashboardHome from './features/dashboard/DashboardHome.jsx';
import OrdersPage from './features/orders/OrdersPage.jsx';
import OrderDetailPage from './features/orders/OrderDetailPage.jsx';
import CircularsPage from './features/circulars/CircularsPage.jsx';
import CircularDetailPage from './features/circulars/CircularDetailPage.jsx';
import GovernmentDecisionsPage from './features/government-decisions/GovernmentDecisionsPage.jsx';
import HolidaysPage from './features/appointments/HolidaysPage.jsx';
import AdminUsersPage from './features/users/AdminUsersPage.jsx';
import CompaniesPage from './features/companies/CompaniesPage.jsx';
import AppointmentsPage from './features/appointments/AppointmentsPage.jsx';
import AppointmentDetailPage from './features/appointments/AppointmentDetailPage.jsx';
import UsersPage from './features/users/UsersPage.jsx';
import UsersDemo from './features/users/UsersDemo.jsx';
import WhatsAppPage from './features/whatsapp/WhatsAppPage.jsx';
import WhatsAppInsidePage from './features/whatsapp/WhatsAppInsidePage.jsx';
import KPIsPage from './features/kpis/KPIsPage.jsx';
import HealthPage from './features/health/HealthPage.jsx';
import AuditPage from './features/audit/AuditPage.jsx';
import SearchPage from './features/search/SearchPage.jsx';
import SettingsPage from './features/settings/SettingsPage.jsx';
import FormsPage from './features/forms/FormsPage.jsx';
import CategoryManagement from './features/forms/CategoryManagement.jsx';
import FormDetailPage from './features/forms/FormDetailPage.jsx';
import SalaryFinancePage from './features/salary-finance/SalaryFinancePage.jsx';
import Form16Page from './features/form16/Form16Page.jsx';
import PrivacyPolicyPage from './features/auth/PrivacyPolicyPage.jsx';

function App() {
  // Check API health
  const { data: healthData, error: healthError } = useQuery({
    queryKey: ['health'],
    queryFn: healthAPI.live,
    refetchInterval: 30000, // Check every 30 seconds
    retry: 1,
  });

  const isApiDown = healthError || healthData?.data?.status !== 'ok';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <ErrorBoundary>
      <Toaster position="top-right"  />
      {isApiDown && <ApiDownBanner />}

      <Routes>
        {/* Public routes */} 
        <Route 
          path="/login" 
          element={
            isAuthenticated() ? <Navigate to="/dashboard" replace /> : <SRPFLoginPage />
          } 
        />
        
        {/* SRPF Login Demo */}
        <Route path="/srpf-login" element={<SRPFLoginDemo />} />
        
        {/* Privacy Policy - accessible without login */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        
     
        {/* Protected routes */}
        <Route path="/" element={<Protected><Shell /></Protected>}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          
          {/* Core application routes */}
          <Route path="dashboard" element={<DashboardHome />} />
          
          <Route path="appointments" element={<AppointmentsPage />} />
          <Route path="holidays" element={<HolidaysPage />} />
          <Route path="appointments/:id" element={<AppointmentDetailPage />} />
          <Route path="orders" element={<OrdersPage />} />
          <Route path="orders/:id" element={<OrderDetailPage />} />
          <Route path="circulars" element={<CircularsPage />} />
          <Route path="circulars/:id" element={<CircularDetailPage />} />
          <Route path="government-decisions" element={<GovernmentDecisionsPage />} />
          <Route path="whatsapp">
            <Route index element={<WhatsAppPage />} />
            <Route path="inside" element={<WhatsAppInsidePage />} />
          </Route>
          <Route path="users" element={<UsersPage />} />
          <Route path="companies" element={<CompaniesPage />} />
          <Route path="kpis" element={<KPIsPage />} />
          <Route path="health" element={<HealthPage />} />
          <Route path="audit" element={<AuditPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="salary-finance" element={<SalaryFinancePage />} />
          <Route path="form16" element={<Form16Page />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="users-demo" element={<UsersDemo />} />
          
          {/* Admin routes */}
          <Route path="admin">
            <Route path="users" element={<AdminUsersPage />} />
          </Route>
          
          {/* Forms routes */}
          <Route path="forms">
            <Route index element={<FormsPage />} />
            <Route path="categories" element={<CategoryManagement />} />
            <Route path=":id" element={<FormDetailPage />} />
          </Route>
        </Route>
        
        {/* 404 - Page Not Found */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </ErrorBoundary>
    </div>
  );
}

export default App;
